/**
 * Ajustes.
 *
 * A ordem da tela segue a frequência de uso, não a hierarquia do sistema: a
 * conta vem primeiro porque é o que qualquer pessoa procura aqui — trocar a
 * senha, sair, ver com que telefone entrou. Antes, "Sair" era a última coisa da
 * tela, depois de todos os parâmetros de negócio da barbearia; quem só queria
 * sair tinha que rolar por cima de sobreposição aceita e antecedência mínima.
 *
 * Para o dono, os parâmetros de negócio continuam aqui embaixo — todos no
 * banco, por barbearia, nenhum em variável de ambiente.
 */

import { SLOT_STEP_OPTIONS } from '@barbearia/core';
import type { PasskeyView, TenantSettingsInput, ThemeMode } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ScreenHeader } from '../App.tsx';
import { Button, ErrorNote, Field, Sheet, Spinner } from '../components/ui.tsx';
import { ApiError, api } from '../lib/api.ts';
import { useRefreshSession, useSession, useTenant } from '../lib/contexts.tsx';
import { phone as formatPhone, fullDateOf } from '../lib/format.ts';
import {
  alreadyRegistered,
  biometricsAvailable,
  registerPasskey,
  wasDismissed,
} from '../lib/passkey.ts';
import { storedPreference } from '../lib/theme.ts';

const THEME_OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: 'SYSTEM', label: 'Do sistema' },
  { value: 'LIGHT', label: 'Claro' },
  { value: 'DARK', label: 'Escuro' },
];

export function Settings() {
  const { tenant, setMode } = useTenant();
  const session = useSession();
  const navigate = useNavigate();
  const refresh = useRefreshSession();

  const [changingPassword, setChangingPassword] = useState(false);
  const [managingPasskeys, setManagingPasskeys] = useState(false);

  const isStaff = session?.membership != null;
  const isOwner = session?.membership?.role === 'OWNER';
  const preference = storedPreference() ?? tenant.themeMode;

  const signOut = useMutation({
    mutationFn: () => api.post('/auth/sign-out'),
    onSuccess: async () => {
      await refresh();
      navigate('/', { replace: true });
    },
  });

  return (
    <div className="flex min-h-full flex-col pb-10">
      <ScreenHeader title="Ajustes" />

      <section className="px-4 pt-5">
        <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          Sua conta
        </h2>

        <div className="overflow-hidden rounded-xl border border-line">
          <div className="border-b border-line bg-surface-raised px-4 py-3">
            <p className="text-[15px] font-medium text-ink">{session?.user.name}</p>
            <p className="text-[13px] text-ink-faint">
              {session?.user.username
                ? `${session.user.username} · ${formatPhone(session.user.phone)}`
                : formatPhone(session?.user.phone ?? '')}
            </p>
          </div>

          <Link
            to="/meus-dados"
            className="flex touch-target items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-3.5 text-left"
          >
            <span className="min-w-0">
              <span className="block text-[15px] text-ink">Meus dados</span>
              <span className="block text-[12px] text-ink-faint">
                {isStaff
                  ? 'Nome, telefone e usuário'
                  : 'Nome, telefone, foto, aniversário e Instagram'}
              </span>
            </span>
            <span className="shrink-0 text-[18px] text-ink-faint">›</span>
          </Link>

          <button
            type="button"
            onClick={() => setChangingPassword(true)}
            className="flex w-full touch-target items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-3.5 text-left"
          >
            <span className="text-[15px] text-ink">Trocar senha</span>
            <span className="text-[18px] text-ink-faint">›</span>
          </button>

          <PasskeyRow onOpen={() => setManagingPasskeys(true)} />

          <button
            type="button"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
            className="flex w-full touch-target items-center bg-surface-raised px-4 py-3.5 text-left text-[15px] font-medium text-danger disabled:opacity-50"
          >
            {signOut.isPending ? 'Saindo…' : 'Sair'}
          </button>
        </div>
      </section>

      <section className="px-4 pt-6">
        <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          Aparência
        </h2>
        <div className="flex gap-2">
          {THEME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setMode(option.value)}
              className={`flex-1 touch-target rounded-xl border px-3 text-[14px] font-medium ${
                preference === option.value
                  ? 'border-primary bg-primary/10 text-ink'
                  : 'border-line bg-surface-raised text-ink-soft'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      {isStaff && (
        <section className="px-4 pt-6">
          <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Sua barbearia
          </h2>
          <div className="overflow-hidden rounded-xl border border-line">
            <LinkRow
              to="/expediente"
              label="Expediente"
              hint="Horários da semana, intervalos e folgas"
            />
            {isOwner && (
              <>
                <LinkRow
                  to="/servicos"
                  label="Serviços"
                  hint="O que a barbearia faz, preço e tempo padrão"
                />
                <LinkRow
                  to="/identidade"
                  label="Identidade visual"
                  hint="Logo, ícone, cores e o que a página pública mostra"
                />
                <LinkRow
                  to="/whatsapp"
                  label="WhatsApp"
                  hint="Quem te chama aparece na busca de agendar, com foto"
                />
              </>
            )}
          </div>
        </section>
      )}

      {isOwner && <BusinessSettings />}

      {/* Por último, e sem título de seção: ninguém abre Ajustes para ler o
          número da versão — abre quando alguma coisa não está batendo. */}
      <section className="px-4 pt-6">
        <div className="overflow-hidden rounded-xl border border-line">
          <LinkRow to="/sobre" label="Sobre o app" hint="Novidades e a versão neste aparelho" />
        </div>
      </section>

      {changingPassword && <ChangePasswordSheet onClose={() => setChangingPassword(false)} />}
      {managingPasskeys && (
        <PasskeysSheet timezone={tenant.timezone} onClose={() => setManagingPasskeys(false)} />
      )}
    </div>
  );
}

/**
 * A porta para a biometria, dentro de "Sua conta".
 *
 * Aparece se o aparelho souber fazer biometria **ou** se a conta já tiver algum
 * aparelho registrado. A segunda metade é o que importa depois de um celular
 * perdido: quem senta no computador do balcão precisa achar a lista para apagar
 * a chave que ficou no aparelho errado, e aquele computador pode não ter leitor
 * nenhum.
 */
function PasskeyRow({ onOpen }: { onOpen: () => void }) {
  const [available, setAvailable] = useState(false);

  const { data } = useQuery({
    queryKey: ['passkeys'],
    queryFn: () => api.get<{ passkeys: PasskeyView[] }>('/auth/passkeys'),
  });

  useEffect(() => {
    let alive = true;
    void biometricsAvailable().then((supported) => {
      if (alive) setAvailable(supported);
    });
    return () => {
      alive = false;
    };
  }, []);

  const registered = data?.passkeys.length ?? 0;
  if (!available && registered === 0) return null;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full touch-target items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-3.5 text-left"
    >
      <span className="min-w-0">
        <span className="block text-[15px] text-ink">Face ID e digital</span>
        <span className="block text-[12px] text-ink-faint">
          {registered === 0
            ? 'Entrar sem digitar senha neste aparelho'
            : `${registered} ${registered === 1 ? 'aparelho' : 'aparelhos'} — entra sem senha`}
        </span>
      </span>
      <span className="shrink-0 text-[18px] text-ink-faint">›</span>
    </button>
  );
}

/**
 * Os aparelhos que entram sem senha.
 *
 * Registrar é opcional e reversível, e a senha continua valendo em todos eles —
 * é o que salva quem trocou de celular, entrou pelo computador ou simplesmente
 * não quis. Remover aqui é o que corta o acesso do aparelho perdido.
 */
function PasskeysSheet({ timezone, onClose }: { timezone: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [available, setAvailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['passkeys'],
    queryFn: () => api.get<{ passkeys: PasskeyView[] }>('/auth/passkeys'),
  });

  useEffect(() => {
    let alive = true;
    void biometricsAvailable().then((supported) => {
      if (alive) setAvailable(supported);
    });
    return () => {
      alive = false;
    };
  }, []);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['passkeys'] });

  const add = useMutation({
    mutationFn: registerPasskey,
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (caught) => {
      // Desistir não é erro; já ter a chave deste aparelho também não — mas
      // esse merece explicação, senão o botão parece não ter feito nada.
      if (wasDismissed(caught)) return;
      if (alreadyRegistered(caught)) {
        setError('Este aparelho já está na lista.');
        return;
      }
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Não foi possível registrar este aparelho. Tente de novo.',
      );
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/passkeys/${id}`),
    onSuccess: invalidate,
  });

  const passkeys = data?.passkeys ?? [];

  return (
    <Sheet
      open
      onClose={onClose}
      title="Face ID e digital"
      footer={
        available ? (
          <Button full loading={add.isPending} onClick={() => add.mutate()}>
            {passkeys.length === 0 ? 'Usar neste aparelho' : 'Adicionar este aparelho'}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {isPending ? (
          <Spinner />
        ) : passkeys.length === 0 ? (
          <p className="text-[15px] text-ink-soft">
            Nenhum aparelho ainda. Registrando este, você entra só com o rosto ou o dedo — sem
            digitar senha.
          </p>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-line">
            {passkeys.map((passkey) => (
              <li
                key={passkey.id}
                className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-3 last:border-b-0"
              >
                <span className="min-w-0">
                  <span className="block text-[15px] text-ink">{passkey.deviceName}</span>
                  <span className="block text-[12px] text-ink-faint">
                    {passkey.lastUsedAt
                      ? `Último uso em ${fullDateOf(passkey.lastUsedAt, timezone)}`
                      : `Registrado em ${fullDateOf(passkey.createdAt, timezone)}`}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => remove.mutate(passkey.id)}
                  disabled={remove.isPending}
                  className="shrink-0 touch-target px-2 text-[14px] font-medium text-danger disabled:opacity-50"
                >
                  Remover
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && <ErrorNote>{error}</ErrorNote>}

        {!available && passkeys.length > 0 && (
          <p className="text-[13px] text-ink-faint">
            Este aparelho não faz biometria — dá para remover os outros por aqui, mas não adicionar
            este.
          </p>
        )}

        <p className="text-[13px] text-ink-faint">
          Sua senha continua valendo em todos os aparelhos. Perdeu o celular? Remova ele desta lista
          e ele deixa de entrar.
        </p>
      </div>
    </Sheet>
  );
}

/**
 * Troca de senha.
 *
 * Pede a senha atual porque o celular do barbeiro fica no balcão, destravado,
 * o dia inteiro. Sem essa confirmação, qualquer pessoa que pegasse o aparelho
 * por trinta segundos trocaria a senha e o dono perderia a própria agenda.
 *
 * A rota já derruba as outras sessões e emite uma nova para este aparelho — é
 * o que faz a troca valer alguma coisa quando o motivo dela é um celular
 * perdido.
 */
function ChangePasswordSheet({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [mismatch, setMismatch] = useState(false);
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () => api.post('/auth/password', { currentPassword, newPassword }),
    onSuccess: () => setDone(true),
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  function submit() {
    setError(null);

    // Conferido aqui porque a confirmação nem chega ao servidor: é uma
    // proteção contra erro de digitação, não uma regra de negócio.
    if (newPassword !== confirmation) {
      setMismatch(true);
      return;
    }

    setMismatch(false);
    change.mutate();
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Trocar senha"
      footer={
        done ? (
          <Button full onClick={onClose}>
            Pronto
          </Button>
        ) : (
          <Button full loading={change.isPending} onClick={submit}>
            Salvar nova senha
          </Button>
        )
      }
    >
      {done ? (
        <p className="py-6 text-center text-[15px] text-ink">
          Senha trocada. Os outros aparelhos que estavam logados foram desconectados.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <Field
            label="Senha atual"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
            error={error?.fieldError('currentPassword')}
          />
          <Field
            label="Nova senha"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
            hint="No mínimo 8 caracteres"
            error={error?.fieldError('newPassword')}
          />
          <Field
            label="Repita a nova senha"
            type="password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="new-password"
            error={mismatch ? 'As duas senhas não são iguais' : undefined}
          />

          {error && !error.fieldError('currentPassword') && !error.fieldError('newPassword') && (
            <ErrorNote>{error.message}</ErrorNote>
          )}

          <p className="text-[13px] text-ink-faint">
            Trocar a senha desconecta os outros aparelhos — inclusive um que você tenha perdido.
          </p>
        </div>
      )}
    </Sheet>
  );
}

/**
 * Os parâmetros que mudam como a agenda se comporta.
 *
 * Cada um deles é uma decisão de negócio da barbearia, não da plataforma: é por
 * isso que moram no banco e são editáveis aqui, e não no .env do servidor.
 */
function BusinessSettings() {
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => api.get<{ settings: TenantSettingsInput }>('/tenant/settings'),
  });

  const update = useMutation({
    mutationFn: (patch: Partial<TenantSettingsInput>) => api.patch('/tenant/settings', patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-settings'] }),
  });

  if (isPending) return <Spinner />;
  if (!data) return null;

  const settings = data.settings;

  return (
    <section className="px-4 pt-6">
      <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
        Regras da agenda
      </h2>

      <div className="overflow-hidden rounded-xl border border-line">
        <NumberRow
          label="Passo dos horários"
          suffix="min"
          value={settings.slotStepMinutes}
          options={[...SLOT_STEP_OPTIONS]}
          hint="De quanto em quanto tempo os horários são oferecidos"
          onChange={(value) => update.mutate({ slotStepMinutes: value })}
        />
        <ToggleRow
          label="Horários fixos"
          hint={
            settings.fixedSlotGrid
              ? 'Os horários contam do começo de cada turno e não mudam com os atendimentos'
              : 'Desligado, o próximo horário começa quando o atendimento anterior termina'
          }
          value={settings.fixedSlotGrid}
          onChange={(value) => update.mutate({ fixedSlotGrid: value })}
        />
        <NumberRow
          label="Sobreposição aceita"
          suffix="min"
          value={settings.overlapToleranceMinutes}
          options={[0, 5, 10, 15, 20]}
          hint="Quanto o próximo cliente pode entrar antes de você terminar"
          onChange={(value) => update.mutate({ overlapToleranceMinutes: value })}
        />
        <NumberRow
          label="Antecedência mínima"
          suffix="min"
          value={settings.minLeadTimeMinutes}
          options={[0, 30, 60, 120, 240]}
          hint="Quão em cima da hora o cliente ainda consegue marcar"
          onChange={(value) => update.mutate({ minLeadTimeMinutes: value })}
        />
        <NumberRow
          label="Prazo de cancelamento"
          suffix="min"
          value={settings.minCancelNoticeMinutes}
          options={[0, 60, 120, 240, 1440]}
          hint="Passado o prazo, só cancelando com você"
          onChange={(value) => update.mutate({ minCancelNoticeMinutes: value })}
        />
        <NumberRow
          label="Agendar até"
          suffix="dias"
          value={settings.maxFutureDays}
          options={[7, 15, 30, 60, 90]}
          hint="Quão longe no futuro o cliente pode marcar"
          onChange={(value) => update.mutate({ maxFutureDays: value })}
        />
        <ToggleRow
          label="Confirmar pedidos automaticamente"
          hint="Desligado, todo pedido espera o seu toque"
          value={settings.autoConfirmRequests}
          onChange={(value) => update.mutate({ autoConfirmRequests: value })}
        />
        <ToggleRow
          label="Cliente escolhe o profissional"
          value={settings.allowClientChooseBarber}
          onChange={(value) => update.mutate({ allowClientChooseBarber: value })}
        />
        <ToggleRow
          label="Aprovar cliente novo"
          hint="Cadastros novos ficam pendentes até você aceitar"
          value={settings.requireNewClientApproval}
          onChange={(value) => update.mutate({ requireNewClientApproval: value })}
        />
      </div>
    </section>
  );
}

function LinkRow({ to, label, hint }: { to: string; label: string; hint: string }) {
  return (
    <Link
      to={to}
      className="flex touch-target items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-3.5 last:border-b-0"
    >
      <span className="min-w-0">
        <span className="block text-[15px] text-ink">{label}</span>
        <span className="block text-[12px] text-ink-faint">{hint}</span>
      </span>
      <span className="shrink-0 text-[18px] text-ink-faint">›</span>
    </Link>
  );
}

function NumberRow({
  label,
  hint,
  suffix,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  suffix: string;
  value: number;
  options: number[];
  onChange: (value: number) => void;
}) {
  return (
    <div className="border-b border-line bg-surface-raised px-4 py-3 last:border-b-0">
      <p className="text-[15px] text-ink">{label}</p>
      {hint && <p className="pb-2 text-[12px] text-ink-faint">{hint}</p>}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`rounded-lg px-3 py-1.5 text-[14px] font-medium tabular-nums ${
              value === option
                ? 'bg-primary text-primary-ink'
                : 'border border-line bg-surface text-ink-soft'
            }`}
          >
            {option} {suffix}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-4 border-b border-line bg-surface-raised px-4 py-3.5 last:border-b-0">
      <span className="min-w-0">
        <span className="block text-[15px] text-ink">{label}</span>
        {hint && <span className="block text-[12px] text-ink-faint">{hint}</span>}
      </span>
      <input
        type="checkbox"
        checked={value}
        onChange={(event) => onChange(event.target.checked)}
        className="h-6 w-6 shrink-0 accent-[var(--color-primary)]"
      />
    </label>
  );
}
